// src/utils/timePickerHelpers.ts

/**
 * 時間選擇器工具函數
 * 提供智能時間建議、時間格式化等功能
 */

export interface QuickTimeOption {
  value: string; // HH:mm 格式（24小時制）
  label: string; // 顯示文字（可能是 12 或 24 小時制）
}

/**
 * 生成智能快捷時間選項
 *
 * 規則：
 * - 第一個選項：當前時間（四捨五入到最近的 interval）
 * - 其他 2 個選項：下一個整點、下一個半點
 *
 * @param baseTime - 基準時間（HH:mm 格式），用於計算建議時間
 * @param interval - 時間間隔（分鐘），預設 15
 * @param formatDisplay - 格式化顯示函數（可選）
 * @returns 3 個快捷時間選項
 *
 * @example
 * // 當前時間 14:37，baseTime = '14:20'
 * generateSmartQuickTimes('14:20', 15)
 * // 返回: [
 * //   { value: '14:30', label: '14:30' },  // 當前時間（四捨五入）
 * //   { value: '15:00', label: '15:00' },  // 下一個整點
 * //   { value: '15:30', label: '15:30' }   // 下一個半點
 * // ]
 */
export function generateSmartQuickTimes(
  baseTime: string,
  interval: number = 15,
  formatDisplay?: (time: string) => string
): QuickTimeOption[] {
  const options: QuickTimeOption[] = [];
  const addedTimes = new Set<string>(); // 用於去重

  // 1. 第一個選項：當前時間（四捨五入到最近的 interval）
  const now = new Date();
  const currentTime = roundToNearestInterval(
    `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
    interval
  );

  options.push({
    value: currentTime,
    label: formatDisplay ? formatDisplay(currentTime) : currentTime,
  });
  addedTimes.add(currentTime);

  // 2. 解析基準時間（用於計算其他建議）
  const [baseHour, baseMinute] = baseTime.split(':').map(Number);
  const baseMinutes = baseHour * 60 + baseMinute;

  // 3. 根據基準時間生成 2 個額外建議（整點和半點）
  const suggestions = getSmartTimeSuggestions(baseMinutes, interval);

  suggestions.forEach(minutes => {
    const hour = Math.floor(minutes / 60) % 24;
    const minute = minutes % 60;
    const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

    // 避免重複，最多 3 個選項
    if (!addedTimes.has(timeStr) && options.length < 3) {
      options.push({
        value: timeStr,
        label: formatDisplay ? formatDisplay(timeStr) : timeStr,
      });
      addedTimes.add(timeStr);
    }
  });

  // 確保至少有 3 個選項（如果不足，補充接下來的時間）
  if (options.length < 3) {
    const lastTime = options[options.length - 1].value;
    const [lastHour, lastMinute] = lastTime.split(':').map(Number);
    let nextMinutes = lastHour * 60 + lastMinute + interval;

    while (options.length < 3) {
      const hour = Math.floor(nextMinutes / 60) % 24;
      const minute = nextMinutes % 60;
      const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

      if (!addedTimes.has(timeStr)) {
        options.push({
          value: timeStr,
          label: formatDisplay ? formatDisplay(timeStr) : timeStr,
        });
        addedTimes.add(timeStr);
      }

      nextMinutes += interval;
    }
  }

  return options.slice(0, 3);
}

/**
 * 根據基準時間生成合理的時間建議
 * 規則：下一個整點、下一個半點
 *
 * @param baseMinutes - 基準時間（總分鐘數）
 * @param interval - 時間間隔（分鐘）
 * @returns 建議時間列表（總分鐘數）
 */
function getSmartTimeSuggestions(
  baseMinutes: number,
  interval: number
): number[] {
  const hour = Math.floor(baseMinutes / 60);
  const minute = baseMinutes % 60;
  const suggestions: number[] = [];

  // 計算下一個整點
  let nextHour: number;
  if (minute > 0) {
    // 如果不在整點，取下一個整點
    nextHour = (hour + 1) * 60;
  } else {
    // 如果已經是整點，取下一個整點
    nextHour = (hour + 1) * 60;
  }
  suggestions.push(nextHour);

  // 計算下一個半點（在下一個整點之後）
  const nextHalfHour = nextHour + 30;
  suggestions.push(nextHalfHour);

  return suggestions;
}

/**
 * 將時間四捨五入到最近的間隔
 *
 * @param time - HH:mm 格式的時間字符串
 * @param interval - 時間間隔（分鐘）
 * @returns 四捨五入後的時間字符串（HH:mm 格式）
 *
 * @example
 * roundToNearestInterval('09:07', 15) // '09:00'
 * roundToNearestInterval('09:08', 15) // '09:15'
 * roundToNearestInterval('09:22', 15) // '09:15'
 * roundToNearestInterval('09:23', 15) // '09:30'
 */
export function roundToNearestInterval(
  time: string,
  interval: number
): string {
  const [h, m] = time.split(':').map(Number);
  const totalMinutes = h * 60 + m;

  // 四捨五入到最近的 interval
  const rounded = Math.round(totalMinutes / interval) * interval;

  // 處理跨日情況（如 23:53 四捨五入到 00:00）
  const hours = Math.floor(rounded / 60) % 24;
  const minutes = rounded % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * 將 HH:mm 格式的時間轉換為總分鐘數
 *
 * @param time - HH:mm 格式的時間字符串
 * @returns 總分鐘數（從 00:00 開始）
 *
 * @example
 * timeToMinutes('09:30') // 570
 * timeToMinutes('14:15') // 855
 */
export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * 將總分鐘數轉換為 HH:mm 格式
 *
 * @param minutes - 總分鐘數（從 00:00 開始）
 * @returns HH:mm 格式的時間字符串
 *
 * @example
 * minutesToTime(570) // '09:30'
 * minutesToTime(855) // '14:15'
 */
export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

/**
 * 系統級常量：時間網格間隔（分鐘）
 * 用於整個系統的時間對齊，包括：
 * - 日曆時間槽
 * - 拖曳對齊
 * - Quick Create 自動對齊
 */
export const TIME_GRID_INTERVAL_MINUTES = 15;

/**
 * 將 Date 對象對齊到時間網格（Date 版本）
 *
 * 對應字串版本：roundToNearestInterval()
 *
 * @param date - 原始時間
 * @param direction - 對齊方向
 *   - 'floor': 向下取整（12:06 → 12:00）
 *   - 'ceil': 向上取整（12:06 → 12:15）
 *   - 'round': 四捨五入（12:06 → 12:00, 12:10 → 12:15）
 * @param interval - 對齊間隔（分鐘），預設 15
 * @returns 對齊後的新 Date 對象
 *
 * @example
 * snapTimeToGrid(new Date('2024-01-15T12:03:00'), 'round') // 12:00
 * snapTimeToGrid(new Date('2024-01-15T12:10:00'), 'round') // 12:15
 * snapTimeToGrid(new Date('2024-01-15T12:10:00'), 'ceil')  // 12:15
 * snapTimeToGrid(new Date('2024-01-15T12:10:00'), 'floor') // 12:00
 */
export function snapTimeToGrid(
  date: Date,
  direction: 'floor' | 'ceil' | 'round' = 'round',
  interval: number = TIME_GRID_INTERVAL_MINUTES
): Date {
  const result = new Date(date);
  const minutes = result.getMinutes();

  let snappedMinutes: number;
  switch (direction) {
    case 'floor':
      snappedMinutes = Math.floor(minutes / interval) * interval;
      break;
    case 'ceil':
      snappedMinutes = Math.ceil(minutes / interval) * interval;
      break;
    case 'round':
    default:
      snappedMinutes = Math.round(minutes / interval) * interval;
  }

  // 處理進位到下一小時
  if (snappedMinutes >= 60) {
    result.setHours(result.getHours() + 1);
    snappedMinutes = 0;
  }

  result.setMinutes(snappedMinutes, 0, 0);
  return result;
}
